import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { APRECIACAO_ROOT, OTHER_FLOW, resolveFlowEligibility } from './eligibility';
import { FlowEligibility, FlowKey } from './types';

interface UseFlowEligibilityParams {
  // Gestor EXIBIDO (internalUser): em lateralidade/substituição a elegibilidade é dele,
  // não a de quem está logado.
  playerId?: string;
  // Ciclo selecionado no seletor da página. Undefined enquanto o AuthContext não resolve
  // os ciclos — nesse intervalo o backend responde pelo ciclo corrente.
  cycleId?: string;
  // Lista carregada do fluxo (rawData). Trocar de referência dispara uma releitura
  // FRESCA, para as pendências das abas refletirem o que acabou de ser apreciado.
  tasks?: unknown[];
  // Fluxo da página que chama.
  flow: FlowKey;
  // Fase em exibição, levada na troca de fluxo para o gestor não voltar à fase corrente.
  currentStage?: string;
}

// Elegibilidade de fluxo da página, resolvida PARA O CICLO SELECIONADO, mais o desvio
// automático quando o ciclo escolhido não tem este fluxo.
//
// A elegibilidade deixou de ser uma propriedade do gestor para ser uma propriedade do par
// (gestor, ciclo): quem só tem realizações MAD no 1S2026 e nada no ciclo recém-aberto é
// elegível ao MAD *no 1S2026*. Como a troca de ciclo re-navega para a própria rota do
// fluxo (sem passar pelo gate), é a página que precisa perceber que o fluxo em que ela
// está não existe no ciclo escolhido — senão o gestor fica olhando uma lista vazia que não
// é a verdade do ciclo, e sem a aba para sair de lá (as abas também dependem desta
// elegibilidade).
export function useFlowEligibility({ playerId, cycleId, tasks, flow, currentStage }: UseFlowEligibilityParams) {
  const navigate = useNavigate();

  // O resultado viaja com o ciclo que o produziu: entre a troca de ciclo e a resposta
  // nova, o valor em mãos é do ciclo ANTERIOR e não pode decidir desvio de rota.
  const [resolved, setResolved] = useState<{ cycleId?: string; eligibility: FlowEligibility | null }>({
    eligibility: null,
  });

  useEffect(() => {
    let cancelled = false;

    if (!playerId) return;

    resolveFlowEligibility(playerId, { fresh: Array.isArray(tasks), cycleId }).then(result => {
      if (!cancelled) setResolved({ cycleId, eligibility: result });
    });

    return () => {
      cancelled = true;
    };
  }, [playerId, cycleId, tasks]);

  const eligibility = resolved.eligibility;
  const isForSelectedCycle = resolved.cycleId === cycleId;

  const shouldLeaveFlow = useMemo(() => {
    if (!eligibility || !isForSelectedCycle) return false;

    const hasCurrent = flow === 'mad' ? eligibility.mad : eligibility.normal;
    const hasOther = flow === 'mad' ? eligibility.normal : eligibility.mad;

    // Sem NENHUM fluxo no ciclo não há para onde ir: a página segue com o empty state,
    // que aqui é a resposta correta.
    return !hasCurrent && hasOther;
  }, [eligibility, flow, isForSelectedCycle]);

  useEffect(() => {
    if (!shouldLeaveFlow) return;

    navigate(`${APRECIACAO_ROOT}/${OTHER_FLOW[flow]}`, { replace: true, state: { stage: currentStage } });
  }, [currentStage, flow, navigate, shouldLeaveFlow]);

  return eligibility;
}
